import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HorseFilters, HorseRead } from '../types/api';
import { apiService } from '../services/api';
import { FilterControls } from './FilterControls';
import { FilterChips } from './FilterChips';
import { formatHorseName } from '../utils/horseName';

interface HorseSelectorProps {
  onSelect: (horse: HorseRead) => void;
  selectedHorseId?: string | null;
  title?: string;
  helperText?: string;
}

/**
 * Lightweight horse lookup that reuses the global filter controls but renders
 * a compact list for selection (name + key metadata). Designed for modals and
 * other flows that need to pick a horse without the full card UI.
 */
export const HorseSelector: React.FC<HorseSelectorProps> = ({
  onSelect,
  selectedHorseId = null,
  title = 'Find a Horse',
  helperText = 'Use the filters below to search all public horses.',
}) => {
  const [filters, setFilters] = useState<HorseFilters>({});
  const [horses, setHorses] = useState<HorseRead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedMap = useMemo(() => new Set([selectedHorseId].filter(Boolean) as string[]), [selectedHorseId]);

  const loadHorses = useCallback(
    async (currentFilters: HorseFilters) => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiService.searchHorses(currentFilters);
        setHorses(response.horses);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load horses');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadHorses(filters);
  }, [filters, loadHorses]);

  const handleApplyFilters = useCallback((newFilters: HorseFilters) => {
    setFilters(newFilters);
  }, []);

  const handleRemoveFilter = useCallback((filterKey: keyof HorseFilters) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[filterKey];
      return next;
    });
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilters({});
  }, []);

  const handleSelect = (horse: HorseRead) => {
    onSelect(horse);
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600">{helperText}</p>
      </div>

      <FilterControls onApplyFilters={handleApplyFilters} currentFilters={filters} />

      <FilterChips filters={filters} onRemoveFilter={handleRemoveFilter} onClearAll={handleClearAllFilters} />

      <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
        {loading && (
          <div className="p-4 text-sm text-gray-600">Loading horses…</div>
        )}
        {error && (
          <div className="p-4 text-sm text-red-600">
            {error}
          </div>
        )}
        {!loading && !error && horses.length === 0 && (
          <div className="p-4 text-sm text-gray-600">No horses match the current filters.</div>
        )}
        {!loading &&
          !error &&
          horses.map((horse) => {
            const isSelected = selectedMap.has(horse.unified_horse_key);
            return (
              <button
                key={horse.unified_horse_key}
                type="button"
                onClick={() => handleSelect(horse)}
                className={`w-full text-left p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 transition ${
                  isSelected ? 'bg-blue-50 border-l-4 border-blue-400' : 'hover:bg-gray-50'
                }`}
              >
                <div>
                  <p className="font-semibold text-gray-900">{formatHorseName(horse.unified_horse_name)}</p>
                  <p className="text-xs text-gray-500">
                    {[horse.unified_studbook, horse.unified_sex, horse.unified_birth_year ?? undefined]
                      .filter(Boolean)
                      .join(' • ')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {isSelected && (
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                      Selected
                    </span>
                  )}
                </div>
              </button>
            );
          })}
      </div>
    </div>
  );
};

